import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { RunSummary } from "@canvas-sync/shared";
import { StatusBadge } from "./StatusBadge";
import { SubmissionBadge } from "./SubmissionBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { api } from "../api/client";
import {
  formatDuration,
  formatIdLabel,
  formatRelative,
  groupByDate,
} from "../lib/format";
import { runSubmissionState, triggerLabel } from "../lib/labels";
import { useRole } from "../lib/roleContext";
import { isRunStuck, stuckThresholdMinutes } from "../lib/runStatus";
import {
  computeInputMismatches,
  computeInputNovelty,
} from "../lib/inputComparison";
import { TermDisplay } from "./TermBadge";
import type { ExpectedTerms } from "../lib/terms";

type Run = RunSummary & { canvasEnv?: "prod" | "test" };

/**
 * Run history rendered as a timeline of collapsible date sections. The
 * shared column header stays anchored at the top; each date bucket is a
 * `<tbody>` whose first row is a click-to-toggle header.
 *
 * Two new bits as of this iteration:
 *   - Submission column showing whether the run's fix CSV reached Canvas
 *     (uses the denormalized `latestUploadStatus` written by the upload
 *     pipeline so there's no per-row join).
 *   - Per-row "Send to Canvas" action on terminal SUCCEEDED runs that have
 *     no upload yet — same path the Run Detail button uses.
 */
export function RunsTimeline({
  runs,
  expectedTerms = {},
}: {
  runs: Run[];
  /** Admin-entered expected 4-digit terms; empty = validation off. */
  expectedTerms?: ExpectedTerms;
}) {
  const { isAdmin } = useRole();
  const groups = useMemo(() => groupByDate(runs, (r) => r.queuedAt), [runs]);
  // RunIds whose ghost count exceeds another run with the same inputMd5 +
  // canvasEnv. Surfaced as a "mismatch" pill next to the Ghosts column.
  const mismatches = useMemo(() => computeInputMismatches(runs), [runs]);
  // Per-run novelty — "is this the first time we've seen this enrollment
  // file, or have we processed it before?" Surfaced on the Sync ID cell.
  const novelty = useMemo(() => computeInputNovelty(runs), [runs]);
  // Default: only the newest (first) group expanded — older days collapsed
  // so the page doesn't grow unbounded as history piles up. Lazy initializer
  // computes from the initial `runs` prop so there's no flash of all-expanded
  // on first paint. User toggles persist within the page visit.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const initialGroups = groupByDate(runs, (r) => r.queuedAt);
    return new Set(initialGroups.slice(1).map((g) => g.key));
  });
  const [submittingRun, setSubmittingRun] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<{
    runId: string;
    message: string;
  } | null>(null);
  // Set when the operator clicks "Send to Canvas" on a run whose fix was
  // already shipped inside a SIS bundle — we warn (but allow) before
  // re-posting, since that would duplicate the ghost fix in Canvas.
  const [confirmRun, setConfirmRun] = useState<Run | null>(null);
  const nav = useNavigate();

  function toggle(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Gate the submit behind a confirm ONLY when the fix is already in Canvas
  // via a SIS bundle; the normal case (nothing shipped yet) submits directly.
  function requestSubmit(r: Run) {
    if (r.sisBundleFixIncluded === true) setConfirmRun(r);
    else void handleSubmit(r.runId);
  }

  async function handleSubmit(runId: string) {
    setSubmitErr(null);
    setSubmittingRun(runId);
    try {
      const { response, canvasError } = await api.submitRunOutput(runId);
      if (canvasError) {
        // Row exists but Canvas rejected — still navigate so the user
        // sees the lifecycle on the upload detail page.
        setSubmitErr({ runId, message: canvasError });
      }
      nav(`/uploads/${response.uploadId}`);
    } catch (e) {
      setSubmitErr({ runId, message: (e as Error).message });
      setSubmittingRun(null);
    }
  }

  return (
    <>
      <table className="runs-table runs-table-grouped">
        <thead>
          <tr>
            <th>Sync ID</th>
            <th>Canvas</th>
            <th>Term</th>
            <th>Status</th>
            <th>Submission</th>
            <th>Started by</th>
            <th>Started</th>
            <th>Duration</th>
            <th>Ghosts</th>
            <th></th>
          </tr>
        </thead>
        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.key);
          return (
            <tbody key={g.key} className="runs-group">
              <tr className="runs-group-head">
                <td colSpan={10}>
                  <button
                    type="button"
                    className="runs-group-toggle"
                    aria-expanded={!isCollapsed}
                    onClick={() => toggle(g.key)}
                  >
                    <span className="runs-group-caret" aria-hidden="true" />
                    <span className="runs-group-title">{g.label}</span>
                    {g.sublabel && (
                      <span className="runs-group-sublabel">{g.sublabel}</span>
                    )}
                    <span className="runs-group-count">
                      {g.items.length} sync{g.items.length === 1 ? "" : "s"}
                    </span>
                  </button>
                </td>
              </tr>
              {!isCollapsed &&
                g.items.map((r) => {
                  const submission = runSubmissionState(r);
                  // 0-ghost runs have no fix CSV to send — hide the button.
                  // Old runs without ghostsFound get the button (we can't tell;
                  // the API endpoint validates and 404s cleanly if there's no
                  // output).
                  const canSubmit =
                    r.status === "SUCCEEDED" &&
                    !r.latestUploadId &&
                    (r.ghostsFound === undefined || r.ghostsFound > 0);
                  const rowSubmitErr =
                    submitErr?.runId === r.runId ? submitErr.message : null;
                  return (
                    <tr key={r.runId}>
                      <td>
                        <Link
                          to={`/syncs/${r.runId}`}
                          className="run-link"
                          title={r.runId}
                        >
                          {formatIdLabel(r.runId)}
                        </Link>
                        <InputNoveltyBadge novelty={novelty.get(r.runId)} />
                      </td>
                      <td>
                        {r.canvasEnv ? (
                          <span className={`env-tag env-tag-${r.canvasEnv}`}>
                            {r.canvasEnv}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <TermDisplay run={r} expectedTerms={expectedTerms} />
                      </td>
                      <td>
                        <StatusBadge status={r.status} />
                        {isRunStuck(r) && (
                          <span
                            className="stuck-tag"
                            title={`In progress for over ${stuckThresholdMinutes()} minutes`}
                          >
                            stuck?
                          </span>
                        )}
                      </td>
                      <td>
                        {r.latestUploadId ? (
                          <Link
                            to={`/uploads/${r.latestUploadId}`}
                            className="muted-link"
                            title={r.latestUploadId}
                          >
                            <SubmissionBadge state={submission} />
                          </Link>
                        ) : (
                          <SubmissionBadge state={submission} />
                        )}
                      </td>
                      <td>
                        <span className="trigger-pill">
                          {triggerLabel(r.trigger)}
                        </span>
                      </td>
                      <td className="muted">{formatRelative(r.queuedAt)}</td>
                      <td className="mono">{formatDuration(r.durationMs)}</td>
                      <td className="mono">
                        {r.ghostsFound ?? "—"}
                        {mismatches.has(r.runId) && (
                          <span
                            className="stuck-tag"
                            title="Found more ghosts than a prior run against the same enrollment file. Open the run to compare."
                            style={{ marginLeft: 6 }}
                          >
                            mismatch
                          </span>
                        )}
                      </td>
                      <td>
                        {isAdmin && canSubmit && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => requestSubmit(r)}
                            disabled={submittingRun === r.runId}
                            title={
                              r.sisBundleFixIncluded === true
                                ? "This run's fix was already shipped via a SIS bundle. Sending again will re-post it to Canvas."
                                : "Send the fix CSV to Canvas as a new upload"
                            }
                          >
                            {submittingRun === r.runId
                              ? "Sending…"
                              : "Send to Canvas"}
                          </button>
                        )}
                        {rowSubmitErr && (
                          <div
                            className="error inline"
                            style={{ marginTop: 4 }}
                          >
                            {rowSubmitErr}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          );
        })}
      </table>
      {confirmRun && (
        <ConfirmDialog
          title="Fix already shipped via SIS bundle"
          message={
            `This sync's ghost-fix CSV was already sent to Canvas inside SIS bundle ` +
            `${confirmRun.sisBundleId ?? "(unknown)"}. Sending it again will re-post the ` +
            `same deletions as a separate SIS import. Only do this if you intend to re-apply it.`
          }
          confirmPhrase="submit again"
          confirmLabel="Send to Canvas anyway"
          cancelLabel="Cancel"
          danger
          busy={submittingRun === confirmRun.runId}
          onConfirm={() => {
            const runId = confirmRun.runId;
            setConfirmRun(null);
            void handleSubmit(runId);
          }}
          onCancel={() => setConfirmRun(null)}
        />
      )}
    </>
  );
}

/**
 * Tiny pill that tells the operator whether this run processed a fresh
 * enrollment file or a repeat of one we've seen before. Renders nothing
 * when the run has no inputMd5 (pre-feature row or worker failed before
 * hashing finished — better to show nothing than a misleading "new").
 */
function InputNoveltyBadge({
  novelty,
}: {
  novelty: { isNew: boolean; priorCount: number; known: boolean } | undefined;
}) {
  if (!novelty || !novelty.known) return null;
  if (novelty.isNew) {
    return (
      <span
        className="badge badge-success"
        style={{ marginLeft: 6, fontSize: 10 }}
        title="First run we've seen for this enrollment file (md5)"
      >
        new input
      </span>
    );
  }
  // priorCount is the number of EARLIER runs — current = priorCount + 1.
  // E.g., priorCount=2 → 3rd occurrence.
  const occurrence = novelty.priorCount + 1;
  return (
    <span
      className="badge badge-cancelled"
      style={{ marginLeft: 6, fontSize: 10 }}
      title={`${ordinal(occurrence)} occurrence — this enrollment file (md5) has been processed ${novelty.priorCount} time${novelty.priorCount === 1 ? "" : "s"} before`}
    >
      repeat ({ordinal(occurrence)})
    </span>
  );
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const lastDigit = n % 10;
  if (lastDigit === 1) return `${n}st`;
  if (lastDigit === 2) return `${n}nd`;
  if (lastDigit === 3) return `${n}rd`;
  return `${n}th`;
}
